import { useContext, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'

import ga4Bigquery1 from 'images/ga4-bigquery-1.png'
import ga4Bigquery3 from 'images/ga4-bigquery-3.png'

import WaveLoading from 'components/WaveLoading'
import Divider from '../Divider'
import InputField from '../InputField'

import AuthorizeButton from './AuthorizeButton'
import './GooglePlayBucket.scss'
import useGetUniqueAccount from 'containers/services/useGetUniqueAccount'
import OrganizationsContext from 'containers/organizations/organizationsContext'

const Guide = ({ onHide, show }) => {
  return (
    <div className={`guide ${show ? 'show' : ''}`}>
      <button onClick={() => onHide()} className="close-button">
        &times;
      </button>
      <h1>Setup Guide</h1>
      <Divider spaceY={10} />
      <h2 style={{ marginBottom: '10px' }}>Prerequisites</h2>
      <p>To connect Google Analytics 4 Events to Maya, you need</p>
      <ol style={{ gap: '0px' }}>
        <li>Access to a A Google Analytics 4 account.</li>
        <li>
          A Google Cloud Platform role with sufficient permissions to provision
          IAM roles for BigQuery access.
        </li>
      </ol>
      <p style={{ lineHeight: '26.4px' }}>
        In your developers settings, make a note of the unique auto-generated
        email address at the bottom of the setup form (i.e,{' '}
        <a href="mailto:" target="_blank" rel="noopener noreferrer">
          ga4events@bi-analytics-253109.iam.gserviceaccount.com
        </a>
        ). You will need this email to configure your source system to sync with
        Maya.
      </p>
      <div className="step-heading">
        <p>STEP 1</p>
        <h2>Create a Google-APIs-Console project and enable BigQuery</h2>
      </div>
      <ol>
        <li>Log in to the <a href="https://console.cloud.google.com/" target="_blank">Google APIs Console.</a></li>
        <li>Create a Google APIs Console project or select an existing project. Make sure that the project does not contain any other sensitive information you don't want to share with Maya.</li>
        <li>Navigate to the APIs table.<br/>Open the Navigation menu in the top-left corner, click <b>APIs & Services</b>, then click <b>Library</b>.</li>
        <li>Activate BigQuery.<br/>Under Google Cloud APIs, click <b>BigQuery API</b>. On the following page, click <b>Enable</b>.</li>
        <li>If prompted, review and agree to the Terms of Service.</li>
      </ol>
      <div className="step-heading">
        <p>STEP 2</p>
        <h2>Prepare your project for BigQuery Export</h2>
      </div>
      <p style={{ lineHeight: '26.4px' }}>If a payment method has not been provided, navigate to the <a href="https://console.cloud.google.com/billing/01B8C5-FEE174-AFAF8B/settings">payment method page</a> to provide payment details. </p>
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 1:</b> Do not export on sandbox mode because it provides limited storage (10GB).
      </p>
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 2:</b> You can find <a href="https://cloud.google.com/bigquery/pricing#storage">storage pricing information here</a>. 
      <br/>No compute costs occur in the process.
      </p>
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 3:</b> If the export is interrupted due to an invalid payment method, we are not able to re-export data for that time.
      </p>
      <div className="step-heading">
        <p>STEP 3</p>
        <h2>Link a Google Analytics 4 property to BigQuery</h2>
      </div>
      <ol>
        <li>In <a href="https://analytics.google.com/analytics/web/#/">Google Analytics</a>, click <a href="https://support.google.com/analytics/answer/6132368?sjid=17092771626506508626-EU">Admin</a></li>
        <li><a href="https://support.google.com/analytics/answer/12813202?sjid=17092771626506508626-EU">Make sure you are in the correct account and property.</a></li>
        <li>In the Property column, under PRODUCT LINKS, click <b>BigQuery Links</b>.</li>
        <li>Click <b>Link</b>.</li>
        <li>Click <b>Choose a BigQuery project</b> to display a list of projects for which you have access. If you have already linked Analytics and Firebase (or plan to), consider exporting to the same Cloud project.</li>
        <li>Select a project from the list, then click <b>Confirm</b>.</li>
        <li>Select the location for the data to be <b>EU</b>. (If your project already has a dataset for the Analytics property, you can't configure this option.)</li>
        <li>Select <b>Configure data streams and events</b> to select which specific events to exclude from the export. If you want to include everything skip this sub step.<br/><br/>You can exclude events by either clicking <b>Add</b> to select from a list of existing events or by clicking <b>Specify event by name</b> to choose existing events by name or to specify event names that have yet to be collected on the property.</li>
        <li>Select <b>Include advertising identifiers for mobile app streams</b> if you want to include advertising identifiers.</li>
        <li>Select <b>Daily</b> (once a day) export of data.</li>
        <li>Review your settings, then click <b>Submit</b>.</li>
      </ol>
      <br />
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 1:</b> The BigQuery link that you create following the guide should have the location EU selected for the dataset transfer as we can not support multi-regional data transfers.
      </p>
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 2:</b> The transfer to Big Query is free of charge.
      </p>
      <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
      <b>NOTE 3:</b> Once the linkage is complete, data should start flowing to your BigQuery project within 24 hours. If you enable daily export, then 1 file will be exported each day that contains the previous day's data (<a href="https://support.google.com/analytics/answer/9823238?hl=en&ref_topic=9359001#zippy=%2Cin-this-article:~:text=the%20time%20zone%20you%20set%20for%20reporting">generally, during early afternoon in the time zone you set for reporting</a>).
      </p>
      <div className="step-heading">
        <p>STEP 4</p>
        <h2>Grant permissions to Maya.</h2>
      </div>
      <ol>
        <li>
          Go to your Google Cloud Platform project{' '}
          <a
            href="https://console.cloud.google.com/iam-admin/iam"
            target="_blank"
            rel="noopener noreferrer"
          >
            IAM section.
          </a>
        </li>
        <li>
          Click + Grant Access to add new user. The Add principals form appears.
        </li>
        <li>
          In the New principals field, enter the auto-generated email address
          you copied in Step 1 (i.e,{' '}
          <a href="mailto:ga4events@bi-analytics-253109.iam.gserviceaccount.com">
            ga4events@bi-analytics-253109.iam.gserviceaccount.com
          </a>
          ).
        </li>
        <li>
          Select the A. Bigquery Job User, B. BigQuery Data Viewer, C. Storage Object Admin. These roles
          provide permissions to:
            <ul>
              <li> 
                Run, enumerate, and cancel our own jobs (updates) Enumerate the
                dataset and tables within a project
              </li>
            </ul>
        </li>
        <p style={{fontWeight: 400, textDecoration: "", lineHeight: "26.4px "}}>
          Maya must have these permissions to access Google Analytics 4 Export
          tables and create the jobs necessary to query the BigQuery tables.
        </p>
        <li>
          Click Save.
          <br />
          <img
            src={ga4Bigquery3}
            style={{
              width: '60%',
              borderRadius: '5px',
              border: '1px solid #dfe1e5',
              height: 'auto',
            }}
          />
        </li>
        <li>
        After the link creation you need to wait until the first run to obtain the Dataset Id, which should take about 24 hours 
        </li>
      </ol>
      <div className="step-heading">
        <p>STEP 5</p>
        <h2>Find BigQuery Project ID and Dataset ID</h2>
      </div>
      <ol>
        <li>Make sure you have completed the linkage and you're viewing this step the next day of the completion.</li>
        <li>
          In your Google Cloud Console, go to your{' '}
          <a
            href="https://console.cloud.google.com/cloud-resource-manager"
            target="_blank"
            rel="nopener noreferrer"
          >
            Cloud Resource Manager
          </a>{' '}
          to see your Projects.
        </li>
        <li>
          Find the Project ID of the project you created in Step 1 and make a
          note of it. You will need it to configure Maya.
          <br />
          <img
            src={ga4Bigquery1}
            style={{
              width: '60%',
              borderRadius: '5px',
              border: '1px solid #dfe1e5',
              height: 'auto',
            }}
          />
        </li>
        <li>The Dataset ID which you will need to input to Maya has the following format:{' '}
<b>analytics_#########</b><br/>You can find it by navigating to the <a href="https://console.cloud.google.com/bigquery" target="_blank">Big Query service page</a> and searching for the newly created dataset on the search bar (type <i>analytics_</i>).</li>
      </ol>
    </div>
  )
}

const GA4 = () => {
  const [showGuide, setShowGuide] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [datasetId, setDatasetId] = useState('')
  const [errorRetrievingUniqueAccount, setErrorRetrievingUniqueAccount] = useState(false)
  const { currentOrganization } = useContext(OrganizationsContext)
  const history = useHistory()
  const serviceUUID = useParams<{id: string}>().id
  const {getUniqueAccount, uniqueAccount} = useGetUniqueAccount()

  const saveData = async () => {
    if (projectId && datasetId) {
      localStorage.setItem('redirectServiceUuid', serviceUUID)
      if ((await getUniqueAccount(currentOrganization.id, projectId, datasetId))?.accounts?.length > 0) {
        history.push(`/organization/${currentOrganization.slug}/sources`)
      } else {
        setErrorRetrievingUniqueAccount(true)
      }
    }
  }

  return (
    <div className="guide-container">
      <Guide onHide={() => setShowGuide(false)} show={showGuide} />

      <img
        src="https://old.mayainsights.com/static/img/logos/ga4-logo.png"
        width="200"
        alt=""
      />
      <p>
        Press the{' '}
        <button style={{verticalAlign: "initial"}} className="button-tip" onClick={() => setShowGuide(true)}>
          Guide
        </button>{' '}
        button to help you through the configuration proccess.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
         <p>Go to <Link to="../../../settings/developers/ga4">Administration &gt; Developers &gt; Google Analytics 4</Link> and click on the copy (<span className="material-icons-outlined saved-icon non-selectable" style={{verticalAlign: "middle"}}>copy</span>) button to copy the unique auto-generated email address.</p> 
      </div>
      <div style={{ display: 'flex', flexDirection: "column", alignItems: 'flex-start', gap: '1rem', width: "50%"}}>
      <Divider spaceY={10}></Divider>
      <h2 style={{fontWeight: "normal"}}>Configure GA4</h2>
      <p style={{ color: "#9b9b9b", fontSize: "14px", margin: "0 "}}>Provide the Project & Dataset ID's that you took note of during the setup process</p>
      <div style={{display: "flex", alignItems: 'center', gap: "2rem"}}>
        <div style={{display: "flex", flexDirection: "column", width: "150%"}}>
          <InputField
            type="text"
            label="Project Id"
            value={projectId}
            setValue={setProjectId}
          />
          <InputField
            type="text"
            label="Dataset Id"
            value={datasetId}
            setValue={setDatasetId}
          /> 
        </div>
        <button style={{margin: 0 }} className="submit-button" disabled={!projectId || !datasetId} onClick={saveData}>Save</button>
      </div>
      </div>

      <div className="guide-bar">
        <button
          className="submit-button guide-button"
          onClick={() => setShowGuide(true)}
        >
          <span>Guide</span>
          <span className="material-icons-outlined saved-icon non-selectable">
            menu_book
          </span>
        </button>
        {errorRetrievingUniqueAccount && (
          <p style={{ color: 'red' }}>
            Unable to retrieve unique account. Please try again.
          </p>
        )}  
      </div>
    </div>
  )
}

export default GA4
